import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createTaskForMeetingApi,
  getTaskApi,
  updateTaskApi,
  deleteTaskApi,
  executeTaskApi,
  generateTaskApi,
  getTaskOptionsApi,
} from "./taskAPI";

export const createTaskForMeeting = createAsyncThunk(
  "TasK/createTaskForMeeting",
  async ({ meetingData, meetingId }, { rejectWithValue }) => {
    try {
      return await createTaskForMeetingApi(meetingData, meetingId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to create task for meeting",
      );
    }
  },
);

export const updateTask = createAsyncThunk(
  "Task/updateTaskStatus",
  async (payload, { rejectWithValue }) => {
    try {
      return await updateTaskApi(payload);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update task status",
      );
    }
  },
);

export const getTask = createAsyncThunk(
  "TasK/getTask",
  async (meetingId, { rejectWithValue }) => {
    try {
      return await getTaskApi(meetingId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to get task for meeting",
      );
    }
  },
);

export const deleteTask = createAsyncThunk(
  "Task/deleteTask",
  async ({ taskId, meetingId }, { rejectWithValue }) => {
    try {
      return await deleteTaskApi({ taskId, meetingId });
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete task");
    }
  },
);

export const executeTask = createAsyncThunk(
  "Task/executeTask",
  async ({ taskData, meetingId }, thunkAPI) => {
    try {
      const data = await executeTaskApi(taskData, meetingId);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const generateTask = createAsyncThunk(
  "TasK/generateTaskForMeeting",
  async ({ prompt, meetingId, agentId }, { rejectWithValue }) => {
    try {
      return await generateTaskApi(meetingId, prompt, agentId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to regenerate task for meeting",
      );
    }
  },
);

export const getTaskOptions = createAsyncThunk(
  "task/getTaskOptions",
  async (
    { settingId, platformType, meetingId },
    { getState, rejectWithValue },
  ) => {
    const key = `${platformType}_${settingId}`;

    try {
      const data = await getTaskOptionsApi(settingId, platformType, meetingId);
      return { key, data: data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed");
    }
  },
);

const tasksSlice = createSlice({
  name: "task",
  initialState: {
    task: null,
    loading: false,
    error: null,
    taskOptions: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ---------- CREATE TASK ----------
      .addCase(createTaskForMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskForMeeting.fulfilled, (state, action) => {
        state.task = action.payload;
        state.loading = false;
      })
      .addCase(createTaskForMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------- UPDATE TASK  ----------
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        const incoming = action.payload;

        if (incoming?.actions && Array.isArray(incoming.actions)) {
          const changedAction = incoming.actions.find(
            (a) => a.uuid === action.meta.arg.uuid,
          );

          if (changedAction && Array.isArray(state.task?.actions)) {
            state.task = {
              ...state.task,
              actions: state.task.actions.map((t) =>
                t.uuid === changedAction.uuid ? changedAction : t,
              ),
            };
          } else {
            state.task = incoming;
          }
        }

        state.loading = false;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------- DELETE TASK ----------
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        const { taskId } = action.meta?.arg || {};

        if (action.payload?.tasks) {
          state.task = action.payload;
        } else if (Array.isArray(state.task?.tasks) && taskId) {
          state.task = {
            ...state.task,
            tasks: state.task.tasks.filter((t) => t?.taskId !== taskId),
          };
        } else {
          state.task = action.payload;
        }

        state.loading = false;
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Task
      .addCase(getTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTask.fulfilled, (state, action) => {
        state.task = action.payload;
        state.loading = false;
      })
      .addCase(getTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Exceute Task
      .addCase(executeTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeTask.fulfilled, (state, action) => {
        state.loading = false;

        const updatedTask = action.payload?.task;
        if (!updatedTask || !state.task?.actions) return;

        const task = state.task.actions.find(
          (t) => t.uuid === updatedTask.uuid,
        );

        if (task) {
          Object.assign(task, updatedTask);
        }
      })

      .addCase(executeTask.rejected, (state, action) => {
        state.loading = false;

        const taskData = action.meta?.arg?.taskData;
        if (!taskData || !state.task?.actions) return;

        const { uuid } = taskData;

        const task = state.task.actions.find((t) => t.uuid === uuid);

        if (task) {
          task.status = "failed";
        }

        state.error = action.payload;
      })

      // regenerate Task for meeting
      .addCase(generateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateTask.fulfilled, (state, action) => {
        state.task = action.payload;
        state.loading = false;
      })
      .addCase(generateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------- GET SYNCED OPTIONS ----------
      .addCase(getTaskOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getTaskOptions.fulfilled, (state, action) => {
        const { key, data } = action.payload;

        state.taskOptions = {
          ...state.taskOptions,
          [key]: data,
        };
      })

      .addCase(getTaskOptions.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch synced options";
      });
  },
});

export default tasksSlice.reducer;
